/**
 * Brain memories data contract — web mirror of `Sources/Models/MemoryModels.swift`.
 *
 * Mirrors the Brain Worker's read-only memories REST API
 * (`claude-brain-mcp` docs/MEMORIES_API.md, `src/memories-api.ts`). These decode
 * straight from the WORKER's JSON (NOT Supabase/PostgREST): the memories surface
 * is served by the Worker and authed with the caller's long-lived `brain_*`
 * bearer token — never the short-lived Supabase JWT.
 *
 * Endpoints:
 *   GET /memories?workspace=&type=&limit=              -> { memories: MemorySummary[] }
 *   GET /memories/:id                                  -> { memory: Memory }
 *   GET /memories/search?q=&workspace=&type=&limit=    -> { memories: MemorySummary[] }
 *
 * Field names mirror the API's snake_case (`updated_at`, `created_at`) into
 * camelCase. Timestamps stay as raw ISO-8601 strings and are formatted for
 * display (see `relativeShort`), matching the Swift client.
 */

import type { Json } from './factory';

/** The fixed set of memory types the API accepts (`unknown` = decode fallback). */
export type MemoryType = 'user' | 'feedback' | 'project' | 'reference' | 'index' | 'unknown';

/** Types offered by the filter UI, in canonical display order. */
export const SELECTABLE_MEMORY_TYPES: MemoryType[] = [
  'user',
  'feedback',
  'project',
  'reference',
  'index',
];

const KNOWN_MEMORY_TYPES: MemoryType[] = [...SELECTABLE_MEMORY_TYPES];

/** Best-effort case over the API's `type` string. */
export function memoryType(raw: string | null | undefined): MemoryType {
  const t = (raw ?? '').toLowerCase();
  return (KNOWN_MEMORY_TYPES as string[]).includes(t) ? (t as MemoryType) : 'unknown';
}

/** A memory list row (workspace-scoped summary). Mirrors `GET /memories`. */
export interface MemorySummary {
  id: string;
  name: string;
  type: string;
  description: string | null;
  workspace: string | null;
  updatedAt: string | null;
}

/** A memory in full (adds content + frontmatter). Mirrors `GET /memories/:id`. */
export interface Memory extends MemorySummary {
  createdAt: string | null;
  content: string | null;
  frontmatter: Json | null;
}

// MARK: - snake_case → camelCase decoders (the API's wire shape)

interface MemorySummaryWire {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  workspace?: string | null;
  updated_at?: string | null;
}

interface MemoryWire extends MemorySummaryWire {
  created_at?: string | null;
  content?: string | null;
  frontmatter?: Json | null;
}

export function decodeMemorySummary(w: MemorySummaryWire): MemorySummary {
  return {
    id: w.id,
    name: w.name,
    type: w.type,
    description: w.description ?? null,
    workspace: w.workspace ?? null,
    updatedAt: w.updated_at ?? null,
  };
}

export function decodeMemory(w: MemoryWire): Memory {
  return {
    ...decodeMemorySummary(w),
    createdAt: w.created_at ?? null,
    content: w.content ?? null,
    frontmatter: w.frontmatter ?? null,
  };
}

/** `{ memories: [...] }` envelope. */
export interface MemoriesListResponse {
  memories: MemorySummaryWire[];
}

/** `{ memory: {...} }` envelope. */
export interface MemoryDetailResponse {
  memory: MemoryWire;
}

// MARK: - UI presentation (mirror of the Swift MemoryType extension)

export function memoryTypeLabel(t: MemoryType): string {
  switch (t) {
    case 'user':
      return 'User';
    case 'feedback':
      return 'Feedback';
    case 'project':
      return 'Project';
    case 'reference':
      return 'Reference';
    case 'index':
      return 'Index';
    default:
      return 'Other';
  }
}

/** A CSS var/color per type (mirrors the Swift color mapping). */
export function memoryTypeColor(t: MemoryType): string {
  switch (t) {
    case 'user':
      return 'var(--blue)';
    case 'feedback':
      return 'var(--orange)';
    case 'project':
      return 'var(--green)';
    case 'reference':
      return 'var(--purple)';
    case 'index':
      return 'var(--indigo)';
    default:
      return 'var(--text-tertiary)';
  }
}

export function memoryTypeIcon(t: MemoryType): string {
  switch (t) {
    case 'user':
      return '👤';
    case 'feedback':
      return '💬';
    case 'project':
      return '🛠';
    case 'reference':
      return '📖';
    case 'index':
      return '🗂';
    default:
      return '📄';
  }
}
